import React, { useState } from 'react';
import { Modal, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import productService from '../../services/productService';

/**
 * Modale de création/édition de catégorie
 */
const CreateCategoryModal = ({ 
  show, 
  onHide, 
  onSuccess, 
  category = null // Pour l'édition
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: category?.name || '',
    description: category?.description || '',
    icon: category?.icon || '📦',
    color: category?.color || '#3b82f6'
  });

  const isEditing = !!category;

  // Options d'icônes prédéfinies
  const iconOptions = [
    '📦', '👜', '🎒', '💼', '👕', '👖', '👟', 
    '📱', '💻', '🎮', '📚', '🏠', '🍔', '🥤',
    '🚗', '⚽', '🎵', '🎨', '💊', '🌱', '🔧', '✨'
  ];

  // Couleurs prédéfinies
  const colorOptions = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b',
    '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16',
    '#f97316', '#6b7280', '#dc2626', '#059669'
  ];

  // Réinitialiser le formulaire quand la modale s'ouvre
  React.useEffect(() => {
    if (show) {
      setFormData({
        name: category?.name || '',
        description: category?.description || '',
        icon: category?.icon || '📦',
        color: category?.color || '#3b82f6'
      });
      setError('');
    }
  }, [show, category]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(''); // Effacer l'erreur lors de la saisie
  };

  const validateForm = () => {
    const errors = [];

    if (!formData.name?.trim()) {
      errors.push('Le nom de la catégorie est requis');
    }

    if (formData.name?.trim().length < 2) {
      errors.push('Le nom doit contenir au moins 2 caractères');
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);

    try {
      const categoryData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        icon: formData.icon,
        color: formData.color
      };


      let response;
      if (isEditing) {
        response = await productService.updateCategory(category.id, categoryData);
      } else {
        response = await productService.createCategory(categoryData);
      }

      if (response.success) {
        onSuccess();
        
        // Réinitialiser le formulaire
        setFormData({
          name: '',
          description: '',
          icon: '📦',
          color: '#3b82f6'
        });
      } else {
        setError(response.message || `Erreur lors de la ${isEditing ? 'mise à jour' : 'création'}`);
      }
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde de la catégorie:', error);
      setError(`Erreur lors de la ${isEditing ? 'mise à jour' : 'création'} de la catégorie`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? 'Modifier la catégorie' : 'Nouvelle Catégorie'}
        </Modal.Title>
      </Modal.Header>
      
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Nom de la catégorie *</Form.Label>
            <Form.Control
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Ex: Électronique, Vêtements, Alimentation..."
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Description de la catégorie (optionnel)..."
            />
          </Form.Group>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Icône</Form.Label>
                <Form.Select
                  value={formData.icon}
                  onChange={(e) => handleInputChange('icon', e.target.value)}
                >
                  {iconOptions.map(icon => (
                    <option key={icon} value={icon}>
                      {icon} {icon}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-muted">
                  Choisissez une icône pour représenter la catégorie
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Couleur</Form.Label>
                <div className="d-flex flex-wrap gap-2 mb-2">
                  {colorOptions.map(color => (
                    <div
                      key={color}
                      className={`border rounded cursor-pointer ${
                        formData.color === color ? 'border-dark border-3' : 'border-light'
                      }`}
                      style={{
                        width: '30px',
                        height: '30px',
                        backgroundColor: color,
                        cursor: 'pointer'
                      }}
                      onClick={() => handleInputChange('color', color)}
                      title={color}
                    />
                  ))}
                </div>
                <Form.Control
                  type="color"
                  value={formData.color}
                  onChange={(e) => handleInputChange('color', e.target.value)}
                />
                <Form.Text className="text-muted">
                  Couleur pour identifier visuellement la catégorie
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Aperçu */}
          <div className="border rounded p-3 bg-light">
            <strong>Aperçu :</strong>
            <div className="mt-2">
              <span 
                className="badge me-2"
                style={{ 
                  backgroundColor: formData.color,
                  color: '#fff',
                  fontSize: '14px'
                }}
              >
                {formData.icon} {formData.name || 'Nom de la catégorie'}
              </span>
            </div>
          </div>
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide} disabled={loading}>
            Annuler
          </Button>
          <Button variant="primary" type="submit" disabled={loading}>
            {loading 
              ? (isEditing ? 'Modification...' : 'Création...') 
              : (isEditing ? 'Modifier la catégorie' : 'Créer la catégorie')
            }
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateCategoryModal;